"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { JobProgress } from "@/components/jobs/job-progress";
import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { CompetitorBriefingStarted, CompetitorOverview, CompetitorStarted, Project } from "@/lib/api/types";
import { unitsCache, unitsCost } from "@/lib/costs";
import { gapSummary, gapUnits, runLine, toggleDomain } from "@/lib/competitors";
import { formatCount } from "@/lib/format";

import { CompetitorList } from "./competitor-list";
import { GapBriefing } from "./gap-briefing";
import { GapResult } from "./gap-result";

type Asking = "list" | "gap" | null;

const PER_DOMAIN_CHOICES = [50, 100, 200, 300];
const FIELD = "h-11 rounded-md border border-field bg-white px-3 text-sm";

export function CompetitorsView() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Übersicht, Auswahl und Rückfragen gehören zum Projekt
  return project ? <View key={project.id} project={project} /> : null;
}

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Unbekannter Fehler";
}

function View({ project }: { project: Project }) {
  const router = useRouter();
  const [overview, setOverview] = useState<CompetitorOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [asking, setAsking] = useState<Asking>(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [perDomainChoice, setPerDomainChoice] = useState<number | null>(null);
  const [minVolumeChoice, setMinVolumeChoice] = useState<number | null>(null);
  const [briefing, setBriefing] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api<CompetitorOverview>(`/api/competitors/overview?project_id=${project.id}`)
        .then((next) => {
          setOverview(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            router.replace(loginUrl("/wettbewerb"));
            return;
          }
          setError(message(caught));
        }),
    [project.id, router],
  );

  useEffect(() => {
    void load();
  }, [load, reload]);

  const limits = overview?.limits;
  const list = overview?.competitors ?? null;
  const known = new Set(list?.competitors.map((c) => c.domain) ?? []);
  const selected = (picked ?? list?.preselected ?? []).filter((domain) => known.has(domain));
  const perDomain = perDomainChoice ?? limits?.per_domain ?? 100;
  const minVolume = minVolumeChoice ?? limits?.min_volume ?? 100;
  const listRunning = overview?.competitors_run?.state === "running";
  const gapRunning = overview?.gap_run?.state === "running";
  const available = overview?.available ?? false;

  const start = async (path: "competitors" | "gap") => {
    setBusy(true);
    setActionError(null);
    try {
      await api<CompetitorStarted>(`/api/competitors/${path}`, {
        method: "POST",
        json:
          path === "gap"
            ? { project_id: project.id, domains: selected, per_domain: perDomain, min_volume: minVolume }
            : { project_id: project.id },
      });
      setAsking(null);
      setReload((current) => current + 1);
    } catch (caught) {
      setActionError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const startBriefing = async (request: { keyword: string; pageType: string }) => {
    setBusy(true);
    setActionError(null);
    try {
      const started = await api<CompetitorBriefingStarted>("/api/competitors/briefing", {
        method: "POST",
        json: { project_id: project.id, keyword: request.keyword, page_type: request.pageType },
      });
      router.push(`/briefing/${started.briefing_id}`);
    } catch (caught) {
      setActionError(message(caught));
      setBusy(false);
    }
  };

  const gapEstimate = gapUnits(selected.length, perDomain);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">Wettbewerber &amp; Content-Gap · Projekt {project.domain}</div>
          <h1 className="text-3xl font-semibold tracking-tight">Wettbewerber</h1>
        </div>
        <button
          type="button"
          aria-expanded={asking === "list"}
          disabled={!available || listRunning || overview === null}
          onClick={() => setAsking(asking === "list" ? null : "list")}
          className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
        >
          {list ? "Wettbewerber neu ermitteln" : "Wettbewerber ermitteln"}
        </button>
      </header>

      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          <span>{error}</span>
          <button type="button" onClick={() => setReload((current) => current + 1)} className="h-11 rounded-md border border-danger bg-white px-3.5">
            Erneut versuchen
          </button>
        </div>
      )}
      {overview && !overview.available && (
        <p role="status" className="rounded-md bg-warning-bg px-4 py-3 text-sm text-warning">
          {overview.reason ?? "ahrefs ist nicht verbunden"}. Wettbewerber und Content-Gap brauchen ahrefs.{" "}
          <Link href="/settings/connections" className="underline">
            Verbindung einrichten
          </Link>
        </p>
      )}
      {actionError && asking !== null && (
        <p role="alert" className="text-sm text-danger">
          {actionError}
        </p>
      )}

      {asking === "list" && limits && (
        <div role="group" aria-label="Wettbewerber ermitteln bestätigen" className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-white px-4 py-3 text-sm">
          <span className="min-w-[280px] flex-1">
            <strong className="font-medium">Wettbewerber ermitteln?</strong> Ein Abruf bei ahrefs (Organic competitors, bis zu {formatCount(limits.list_limit)} Domains
            nach gemeinsamen Keywords). Kosten: {unitsCost(limits.competitors_units)}. {unitsCache}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => void start("competitors")}
            className="h-11 rounded-md bg-accent px-3.5 text-[13px] font-medium text-white disabled:opacity-60"
          >
            Wettbewerber ermitteln
          </button>
          <button type="button" onClick={() => setAsking(null)} className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]">
            Abbrechen
          </button>
        </div>
      )}

      {listRunning && overview?.competitors_run?.job_id && (
        <JobProgress
          key={overview.competitors_run.job_id}
          jobId={overview.competitors_run.job_id}
          title="Wettbewerber werden ermittelt"
          onFinished={() => setReload((current) => current + 1)}
        />
      )}
      {overview?.competitors_run?.state === "failed" && (
        <p role="alert" className="rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          Die Ermittlung der Wettbewerber ist fehlgeschlagen{overview.competitors_run.error ? `: ${overview.competitors_run.error}` : "."}
        </p>
      )}

      {overview === null && !error && <p className="text-sm text-muted">Wettbewerber werden geladen …</p>}

      {overview && list === null && !listRunning && (
        <p className="rounded-lg border border-line bg-white px-5 py-6 text-sm text-muted">
          Noch keine Wettbewerber ermittelt. „Wettbewerber ermitteln“ holt die organischen Wettbewerber von {project.domain} aus ahrefs. Danach wählst du
          bis zu {formatCount(limits?.max_domains ?? 5)} davon für die Content-Gap.
        </p>
      )}

      {list && limits && (
        <section aria-labelledby="competitors-title" className="flex flex-col gap-3 rounded-lg border border-line bg-white px-5 py-4">
          <div className="flex flex-col gap-0.5">
            <h2 id="competitors-title" className="text-base font-semibold">
              Organische Wettbewerber
            </h2>
            <span className="text-xs text-muted">
              Domains, die für dieselben Keywords ranken wie {list.own_domain}
              {overview?.competitors_run?.state === "done" ? ` · ${runLine(overview.competitors_run)}` : ""}. Plattformen und Verzeichnisse sind nicht
              vorgewählt.
            </span>
          </div>
          <CompetitorList list={list} selected={selected} max={limits.max_domains} onToggle={(domain) => setPicked(toggleDomain(selected, domain, limits.max_domains))} />
        </section>
      )}

      {list && limits && (
        <section aria-labelledby="gap-title" className="flex flex-col gap-4 rounded-lg border border-line bg-white px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <h2 id="gap-title" className="text-base font-semibold">
                Content-Gap
              </h2>
              <span className="text-xs text-muted">
                Keywords, für die die gewählten Wettbewerber in den Top 10 stehen und {list.own_domain} nicht oder erst ab Platz 11.
              </span>
            </div>
            <button
              type="button"
              aria-expanded={asking === "gap"}
              disabled={!available || gapRunning || selected.length === 0}
              onClick={() => setAsking(asking === "gap" ? null : "gap")}
              className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
            >
              Content-Gap berechnen
            </button>
          </div>

          {asking === "gap" && (
            <div role="group" aria-label="Content-Gap berechnen bestätigen" className="flex flex-col gap-3 rounded-md border border-line px-4 py-3 text-sm">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <label htmlFor="gap-per-domain" className="text-[13px] font-medium">
                    Keywords je Wettbewerber
                  </label>
                  <select id="gap-per-domain" value={perDomain} onChange={(event) => setPerDomainChoice(Number(event.target.value))} className={FIELD}>
                    {PER_DOMAIN_CHOICES.map((choice) => (
                      <option key={choice} value={choice}>
                        {formatCount(choice)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="gap-min-volume" className="text-[13px] font-medium">
                    Mindest-Suchvolumen
                  </label>
                  <input
                    id="gap-min-volume"
                    type="number"
                    min={0}
                    step={50}
                    value={minVolume}
                    onChange={(event) => setMinVolumeChoice(Math.max(0, Number(event.target.value) || 0))}
                    className={`${FIELD} w-36`}
                  />
                </div>
              </div>
              <p>
                <strong className="font-medium">Content-Gap für {selected.join(", ")} berechnen?</strong> Je Wettbewerber ein Abruf mit den Top-10-Keywords, danach
                der Abgleich mit den eigenen Rankings. Kosten: {unitsCost(gapEstimate)}. {unitsCache}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || selected.length === 0}
                  onClick={() => void start("gap")}
                  className="h-11 rounded-md bg-accent px-3.5 text-[13px] font-medium text-white disabled:opacity-60"
                >
                  Content-Gap starten
                </button>
                <button type="button" onClick={() => setAsking(null)} className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]">
                  Abbrechen
                </button>
              </div>
            </div>
          )}

          {selected.length === 0 && <p className="text-sm text-muted">Wähle mindestens einen Wettbewerber in der Liste.</p>}

          {gapRunning && overview?.gap_run?.job_id && (
            <JobProgress
              key={overview.gap_run.job_id}
              jobId={overview.gap_run.job_id}
              title="Content-Gap wird berechnet"
              onFinished={() => setReload((current) => current + 1)}
            />
          )}
          {overview?.gap_run?.state === "failed" && (
            <p role="alert" className="rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
              Die Content-Gap ist fehlgeschlagen{overview.gap_run.error ? `: ${overview.gap_run.error}` : "."}
            </p>
          )}

          {briefing !== null && (
            <GapBriefing
              key={briefing}
              keyword={briefing}
              busy={busy}
              error={actionError}
              onStart={(request) => void startBriefing(request)}
              onCancel={() => setBriefing(null)}
            />
          )}

          {overview?.gap ? (
            <>
              <div className="text-xs text-muted">
                {gapSummary(overview.gap)} · Wettbewerber: {overview.gap.domains.join(", ")}
                {overview.gap_run?.state === "done" ? ` · ${runLine(overview.gap_run)}` : ""}
              </div>
              <GapResult gap={overview.gap} onBriefing={(keyword) => { setActionError(null); setBriefing(keyword); }} />
            </>
          ) : (
            !gapRunning && <p className="text-sm text-muted">Noch keine Content-Gap berechnet.</p>
          )}
        </section>
      )}
    </div>
  );
}
